import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, ArrowUpRight, LayoutGrid } from 'lucide-react';
import Logo from './Logo';

// BCG-inspired mega-menu content, mapped to DigitalWorks' IA.
const MEGA = {
  'What We Do': {
    columns: [
      {
        title: 'Offerings',
        links: [
          { label: 'AI Strategy & Advisory', to: '/what-we-do/ai-strategy-and-advisory' },
          { label: 'Engineering & Agent-Assisted Delivery', to: '/what-we-do/ai-native-engineering' },
          { label: 'Platform & Commerce Modernisation', to: '/what-we-do/platform-and-commerce-modernisation' },
          { label: 'Run & Optimise', to: '/what-we-do/run-and-optimise' },
        ],
      },
      {
        title: 'By Industry',
        links: [
          { label: 'Retail & Commerce', to: '/industries/retail-and-commerce' },
          { label: 'All industries →', to: '/what-we-do' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Latest',
      title: 'The Adoption Sprint',
      body: 'Eight weeks. Fixed price. One product area onboarded to Tests as Spec.',
      to: '/approach',
    },
  },
  NeuralWorks: {
    columns: [
      {
        title: 'The framework',
        links: [
          { label: 'Overview', to: '/neuralworks' },
          { label: 'How it works — Tests as Spec', to: '/neuralworks/how-it-works' },
          { label: 'Accelerators', to: '/neuralworks/accelerators' },
          { label: 'Security & Governance', to: '/neuralworks/security-and-governance' },
        ],
      },
      {
        title: 'Explore',
        links: [
          { label: 'The Ascent — five stages', to: '/approach' },
          { label: 'AI Readiness Diagnostic', to: '/tools/ai-readiness-diagnostic' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Read',
      title: 'A governance model for agents that act',
      body: 'The risk taxonomy and four-tier gate system, published in full.',
      to: '/insights/governance-for-agents',
    },
  },
  Work: {
    columns: [
      {
        title: 'Case studies',
        links: [
          { label: 'A digital strategy that reads as one Sephora, in the UAE', to: '/work/sephora' },
          { label: 'The Blue SuperApp — one experience across the group', to: '/work/al-futtaim' },
          { label: 'A unified omnichannel eye-care experience', to: '/work/magrabi' },
          { label: 'A borderless, brand-plural digital strategy', to: '/work/fenix' },
          { label: 'Running NeuralWorks on ourselves first', to: '/work/digitalworks-internal' },
        ],
      },
      {
        title: 'By outcome',
        links: [
          { label: 'All case studies →', to: '/work' },
          { label: 'The AI Readiness Diagnostic', to: '/tools/ai-readiness-diagnostic' },
        ],
      },
    ],
    featured: {
      eyebrow: 'By outcome',
      title: 'Clients. Measured results.',
      body: 'We ran NeuralWorks on ourselves before we offered it to anyone else.',
      to: '/work',
    },
  },
  Insights: {
    columns: [
      {
        title: 'Featured writing',
        links: [
          { label: 'From Insight to Impact', to: '/insights/insight-to-impact' },
          { label: 'The GenAI Value Blueprint for 2025', to: '/insights/genai-value-blueprint' },
          { label: 'The Composable Commerce Handbook', to: '/insights/composable-commerce-handbook' },
          { label: 'Rethinking Content and Commerce', to: '/insights/rethinking-content-commerce' },
        ],
      },
      {
        title: 'Formats',
        links: [
          { label: 'All insights', to: '/insights' },
          { label: 'Future-Proofing Retail (Guide)', to: '/insights/future-of-retail' },
          { label: 'Handbooks & blueprints', to: '/insights' },
        ],
      },
    ],
    featured: {
      eyebrow: 'New',
      title: 'From Insight to Impact',
      body: 'The five AI use cases moving from pilot to production in omnichannel retail.',
      to: '/insights/insight-to-impact',
    },
  },
  About: {
    columns: [
      {
        title: 'Company',
        links: [
          { label: 'About DigitalWorks', to: '/about' },
          { label: 'How we came to be', to: '/about' },
        ],
      },
      {
        title: 'Join us',
        links: [
          { label: 'How the role is changing', to: '/careers' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Join us',
      title: 'Join us at DigitalWorks.',
      body: 'The engineering job is changing. We would rather build the new one than defend the old one.',
      to: '/careers',
    },
  },
};

const SHORTCUTS = [
  { label: 'Book a working session', to: '/contact', primary: true },
  { label: 'Take the AI Readiness Diagnostic', to: '/tools/ai-readiness-diagnostic' },
  { label: 'Read: From Insight to Impact', to: '/insights/insight-to-impact' },
  { label: 'A digital strategy that reads as one Sephora, in the UAE', to: '/work/sephora' },
  { label: 'Data residency by jurisdiction', to: '/trust' },
  { label: 'Join us at DigitalWorks', to: '/careers' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveMega(null);
    setShortcutsOpen(false);
  }, [pathname]);

  const openMega = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMega(name);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveMega(null), 140);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-500 ease-out ${
        scrolled || activeMega
          ? 'bg-[rgba(251,250,246,0.88)] backdrop-blur-xl backdrop-saturate-150 border-b border-[#E8E4D9] shadow-[0_1px_0_0_rgba(255,255,255,0.6)_inset,0_8px_24px_-18px_rgba(26,26,26,0.18)]'
          : 'bg-[rgba(251,250,246,0.55)] backdrop-blur-md backdrop-saturate-150 border-b border-[#E8E4D9]/55 shadow-[0_1px_0_0_rgba(255,255,255,0.55)_inset]'
      }`}
      onMouseLeave={scheduleClose}
    >
      {/* Subtle top highlight strip — the Apple-style glass edge */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 15%, rgba(255,255,255,0.75) 85%, rgba(255,255,255,0) 100%)',
        }}
      />
      <div className="dwx flex items-center justify-between h-[68px]">
        {/* Left: shortcuts button + primary nav */}
        <div className="flex items-center gap-7 flex-1">
          <nav className="hidden lg:flex items-center gap-7">
            {Object.keys(MEGA).map((name) => {
              const active = activeMega === name;
              return (
                <div
                  key={name}
                  onMouseEnter={() => openMega(name)}
                  className="relative"
                >
                  <Link
                    to={MEGA[name].columns[0].links[0].to}
                    className={`inline-flex items-center gap-1 whitespace-nowrap text-[13px] tracking-tight transition-colors ${active ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]/72 hover:text-[#1A1A1A]'}`}
                  >
                    <span>{name}</span>
                    <ChevronRight size={12} className={`mt-0.5 transition-transform duration-300 ${active ? 'rotate-90' : ''}`} />
                  </Link>
                  {active && (
                    <span className="absolute left-0 right-0 -bottom-[20px] h-[2px] bg-[#22A94F]"></span>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Centered logo */}
        <Link to="/" className="flex items-center" aria-label="DigitalWorks home" onMouseEnter={scheduleClose}>
          <Logo size={38} />
        </Link>

        {/* Right: CTA */}
        <div className="hidden lg:flex items-center gap-5 flex-1 justify-end">
          <Link
            to="/contact"
            data-testid="header-book-session"
            className="plausible-event-name=Book+Session+Header group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#1A1A1A] text-[#FBFAF6] text-[12.5px] font-medium hover:bg-[#22A94F] transition-colors relative"
          >
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22A94F] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22A94F]"></span>
            </span>
            Book a session
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* MEGA MENU PANEL */}
      <div
        className={`hidden lg:block absolute left-0 right-0 top-full bg-[#FBFAF6] border-b border-[#E8E4D9] shadow-[0_18px_36px_-24px_rgba(0,0,0,0.15)] overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${activeMega ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
        onMouseEnter={() => activeMega && openMega(activeMega)}
      >
        {activeMega && (
          <div className="dwx py-10 grid grid-cols-12 gap-10">
            <div className="col-span-8 grid grid-cols-2 gap-10">
              {MEGA[activeMega].columns.map((col) => (
                <div key={col.title}>
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/50 mb-5">
                    {col.title}
                  </div>
                  <ul className="space-y-3">
                    {col.links.map((l) => (
                      <li key={l.to + l.label}>
                        <Link
                          to={l.to}
                          onClick={() => setActiveMega(null)}
                          className="group inline-flex items-start gap-2 text-[15px] text-[#1A1A1A]/85 hover:text-[#22A94F] transition-colors"
                        >
                          <span>{l.label}</span>
                          <ArrowUpRight size={14} className="mt-1 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="col-span-4">
              <Link
                to={MEGA[activeMega].featured.to}
                onClick={() => setActiveMega(null)}
                className="block rounded-[16px] bg-[#F4F2EC] hover:bg-[#E8E4D9] transition-colors p-6 h-full"
              >
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">
                  {MEGA[activeMega].featured.eyebrow}
                </div>
                <div className="serif text-[24px] leading-tight text-[#1A1A1A]">
                  {MEGA[activeMega].featured.title}
                </div>
                <p className="mt-3 text-[13px] leading-relaxed text-[#1A1A1A]/70">
                  {MEGA[activeMega].featured.body}
                </p>
                <div className="mt-6 inline-flex items-center gap-1 text-[12px] font-medium text-[#178838]">
                  Read more <ArrowUpRight size={14} />
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* SHORTCUTS DRAWER */}
      {shortcutsOpen && (
        <>
          <div className="fixed inset-0 z-[60] bg-[#1A1A1A]/30" onClick={() => setShortcutsOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 z-[70] w-full max-w-[420px] bg-[#FBFAF6] border-r border-[#E8E4D9] p-8 overflow-y-auto animate-[slideIn_320ms_cubic-bezier(0.22,1,0.36,1)]">
            <div className="flex items-center justify-between mb-10">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Shortcuts</div>
              <button onClick={() => setShortcutsOpen(false)} className="p-1" aria-label="Close shortcuts">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-2">
              {SHORTCUTS.map((s) => (
                <Link
                  key={s.to + s.label}
                  to={s.to}
                  onClick={() => setShortcutsOpen(false)}
                  className={`group flex items-center justify-between px-5 py-4 rounded-[14px] border transition-colors ${s.primary ? 'bg-[#1A1A1A] border-[#1A1A1A] text-[#FBFAF6] hover:bg-[#22A94F] hover:border-[#22A94F]' : 'bg-transparent border-[#E8E4D9] hover:border-[#22A94F] text-[#1A1A1A]'}`}
                >
                  <span className="text-[14px] font-medium">{s.label}</span>
                  <ArrowUpRight size={16} className={`transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${s.primary ? 'text-[#FBFAF6]' : 'text-[#1A1A1A]/50 group-hover:text-[#22A94F]'}`} />
                </Link>
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-[#E8E4D9] text-[12px] text-[#1A1A1A]/60">
              Or jump to: <Link to="/what-we-do" className="link-sweep">What We Do</Link> · <Link to="/neuralworks" className="link-sweep">NeuralWorks</Link> · <Link to="/work" className="link-sweep">Work</Link>
            </div>
          </aside>
        </>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#FBFAF6] border-t border-[#E8E4D9]">
          <div className="dwx py-6 flex flex-col gap-3">
            {Object.entries(MEGA).map(([name, cfg]) => (
              <details key={name} className="border-b border-[#E8E4D9] pb-3">
                <summary className="text-[16px] py-2 cursor-pointer flex justify-between items-center">
                  <span>{name}</span>
                  <ChevronRight size={16} />
                </summary>
                <ul className="mt-2 pl-3 space-y-2">
                  {cfg.columns.flatMap((c) => c.links).map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} className="block py-1.5 text-[14px] text-[#1A1A1A]/75">{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
            <Link to="/contact" data-testid="mobile-book-session" className="plausible-event-name=Book+Session+Mobile mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#1A1A1A] text-[#FBFAF6]">
              <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22A94F] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22A94F]"></span>
              </span>
              Book a session
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
